/**
 * Mix & Match Bundle Page
 * Handles state management, product switching, and cart functionality
 */

class MixMatch {
  constructor(section) {
    this.section = section;
    this.config = this.parseConfig();

    // State
    this.state = {
      productHandle: this.config.defaultProductHandle,
      productData: null,
      selectedSize: null,
      bundleSize: 'medium', // small, medium, large
      items: [], // Array of { color: string, quantity: number }
    };

    // Cache DOM elements
    this.elements = {
      bundlePreview: section.querySelector('[data-bundle-preview]'),
      bundleGrid: section.querySelector('[data-bundle-grid]'),
      colorSelector: section.querySelector('[data-color-selector]'),
      colorGrid: section.querySelector('[data-color-grid]'),
      sizeSelector: section.querySelector('[data-size-selector]'),
      sizeButtons: section.querySelector('[data-size-buttons]'),
      bundleSelector: section.querySelector('[data-bundle-selector]'),
      stickyBar: section.querySelector('[data-sticky-bar]'),
      warning: section.querySelector('[data-warning]'),
      warningText: section.querySelector('[data-warning-text]'),
      upgradeBanner: section.querySelector('[data-upgrade-banner]'),
      downgradeBanner: section.querySelector('[data-downgrade-banner]'),
      productDropdown: section.querySelector('[data-product-dropdown]'),
      productList: section.querySelector('[data-product-list]'),
      addToCartBtn: section.querySelector('[data-add-to-cart]'),
      sizeSelect: section.querySelector('[data-size-select]'),
      bundleSelect: section.querySelector('[data-bundle-select]'),
      finalPriceMobile: section.querySelector('[data-final-price-mobile]'),
      originalPriceMobile: section.querySelector('[data-original-price-mobile]'),
      sizeGuideDrawer: section.querySelector('[data-size-guide-drawer]'),
      sizeGuideTrigger: section.querySelector('[data-size-guide-trigger]'),
      sizeGuideClose: section.querySelectorAll('[data-size-guide-close]'),
    };

    // Templates
    this.templates = {
      filledSlot: section.querySelector('[data-slot-template="filled"]'),
      emptySlot: section.querySelector('[data-slot-template="empty"]'),
    };

    this.init();
  }

  parseConfig() {
    const configEl = this.section.querySelector('[data-mix-match-config]');
    if (!configEl) return {};
    try {
      return JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Failed to parse mix-match config:', e);
      return {};
    }
  }

  async init() {
    // Load initial product data
    await this.loadProduct(this.state.productHandle);

    // Set initial size (first available)
    this.setInitialSize();

    // Bind events
    this.bindEvents();

    // Initial render
    this.render();

    // Populate product dropdown
    this.populateProductDropdown();

    // Update bundle select options with config values
    this.updateBundleSelectOptions();
  }

  updateBundleSelectOptions() {
    if (!this.elements.bundleSelect || !this.config.bundles) return;

    const smallOption = this.elements.bundleSelect.querySelector('[data-bundle-option-small]');
    const mediumOption = this.elements.bundleSelect.querySelector('[data-bundle-option-medium]');
    const largeOption = this.elements.bundleSelect.querySelector('[data-bundle-option-large]');

    const unit = this.config.translations?.bundleUnit || 'st';

    if (smallOption) smallOption.textContent = `${this.config.bundles.small.qty} ${unit}`;
    if (mediumOption) mediumOption.textContent = `${this.config.bundles.medium.qty} ${unit}`;
    if (largeOption) largeOption.textContent = `${this.config.bundles.large.qty} ${unit}`;
  }

  updateBundleCards() {
    const product = this.config.products?.find(p => p.handle === this.state.productHandle);
    if (!product?.packs) return;

    ['small', 'medium', 'large'].forEach(size => {
      const pack = product.packs[size];
      const option = this.section.querySelector(`[data-bundle-option][data-bundle-size="${size}"]`);
      if (!pack || !option) return;

      const hasSave = pack.compareAt > pack.price;

      const finalEl = option.querySelector('[data-bundle-final-price]');
      if (finalEl) finalEl.textContent = pack.finalMoney;

      const originalEl = option.querySelector('[data-bundle-original-price]');
      if (originalEl) {
        originalEl.textContent = hasSave ? pack.compareMoney : '';
        originalEl.hidden = !hasSave;
      }

      const saveBadge = option.querySelector('[data-bundle-save-badge]');
      if (saveBadge) {
        const saveAmount = saveBadge.querySelector('[data-bundle-save]');
        if (saveAmount) saveAmount.textContent = hasSave ? pack.saveMoney : '';
        saveBadge.hidden = !hasSave;
        // Keep the CSS class in sync with the attribute — the class also hides the
        // badge (display:none), so toggling only the attribute could leave it stuck.
        saveBadge.classList.toggle('bundle-selector__badge--hidden', !hasSave);
      }

      const unitEl = option.querySelector('[data-bundle-unit-price]');
      if (unitEl && pack.perMoney) unitEl.textContent = pack.perMoney;

      // Image priority: theme-editor bundle image (when filled) overrides the pack image
      const manualImage = this.config.bundleImages?.[size];
      const imageWrap = option.querySelector('[data-bundle-image]');
      if (imageWrap) {
        if (manualImage) {
          imageWrap.innerHTML = `<img src="${manualImage}" alt="" loading="lazy" width="60" height="60">`;
        } else if (pack.image) {
          imageWrap.innerHTML = `<img src="${pack.image}" alt="" loading="lazy" width="60" height="60">`;
        } else if (this.state.productData?.featured_image) {
          imageWrap.innerHTML = `<img src="${this.getImageUrl(this.state.productData.featured_image, 120)}" alt="" loading="lazy" width="60" height="60">`;
        } else {
          imageWrap.innerHTML = '<div class="bundle-selector__image-placeholder"></div>';
        }
      }
    });
  }

  async loadProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      this.state.productData = await response.json();
      this.state.productHandle = handle;

      // Reset items when product changes
      this.state.items = [];

      // Update color selector
      this.updateColorSelector();

      // Update size selector
      this.updateSizeSelector();

      // Update bundle selector cards (image + prices) for this product
      this.updateBundleCards();

      return this.state.productData;
    } catch (error) {
      console.error('Failed to load product:', error);
      return null;
    }
  }

  setInitialSize() {
    if (!this.state.productData) return;

    const sizeOption = this.state.productData.options[1]; // Second option is size
    if (!sizeOption) return;

    // Find first available size
    const variants = this.state.productData.variants;
    for (const variant of variants) {
      if (variant.available) {
        this.state.selectedSize = variant.option2;
        break;
      }
    }

    // Fallback to first size
    if (!this.state.selectedSize && variants.length > 0) {
      this.state.selectedSize = variants[0].option2;
    }
  }

  bindEvents() {
    // Color quantity buttons
    this.section.addEventListener('click', (e) => {
      const increaseBtn = e.target.closest('[data-qty-increase]');
      const decreaseBtn = e.target.closest('[data-qty-decrease]');
      const removeBtn = e.target.closest('[data-remove-item]');

      if (increaseBtn) {
        const card = increaseBtn.closest('[data-color-card]');
        const color = card?.dataset.color;
        if (color) this.updateColorQuantity(color, 1);
      }

      if (decreaseBtn) {
        const card = decreaseBtn.closest('[data-color-card]');
        const color = card?.dataset.color;
        if (color) this.updateColorQuantity(color, -1);
      }

      if (removeBtn) {
        const slot = removeBtn.closest('[data-slot]');
        const color = slot?.dataset.color;
        if (color) this.updateColorQuantity(color, -1);
      }
    });

    // Size buttons
    this.elements.sizeButtons?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size-button]');
      if (btn && !btn.disabled) {
        this.setSize(btn.dataset.size);
      }
    });

    // Size select (mobile)
    this.elements.sizeSelect?.addEventListener('change', (e) => {
      this.setSize(e.target.value);
    });

    // Bundle select (mobile)
    this.elements.bundleSelect?.addEventListener('change', (e) => {
      this.setBundleSize(e.target.value);
      // Also update the radio buttons to stay in sync
      const radio = this.section.querySelector(`[data-bundle-radio][value="${e.target.value}"]`);
      if (radio) radio.checked = true;
    });

    // Bundle size radios - prevent scroll on click
    let savedScrollY = 0;
    this.elements.bundleSelector?.addEventListener('click', () => {
      savedScrollY = window.scrollY;
    }, true);

    this.elements.bundleSelector?.addEventListener('change', (e) => {
      const radio = e.target.closest('[data-bundle-radio]');
      if (radio) {
        this.setBundleSize(radio.value);
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollY);
        });
      }
    });

    // Upgrade banner
    this.elements.upgradeBanner?.addEventListener('click', () => {
      const scrollY = window.scrollY;
      this.upgradeBundle();
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    });

    // Downgrade banner
    this.elements.downgradeBanner?.addEventListener('click', () => {
      const scrollY = window.scrollY;
      this.downgradeBundle();
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    });

    // Product dropdown
    const dropdownTrigger = this.section.querySelector('[data-product-dropdown-trigger]');
    dropdownTrigger?.addEventListener('click', () => {
      this.toggleProductDropdown();
    });

    // Close dropdown on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-product-selector-wrapper]')) {
        this.closeProductDropdown();
      }
    });

    // Warning close
    const warningClose = this.section.querySelector('[data-warning-close]');
    warningClose?.addEventListener('click', () => {
      this.elements.warning?.setAttribute('hidden', '');
    });

    // Add to cart
    this.elements.addToCartBtn?.addEventListener('click', () => {
      this.addToCart();
    });

    // Size guide drawer
    this.elements.sizeGuideTrigger?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openSizeGuide();
    });

    this.elements.sizeGuideClose?.forEach(btn => {
      btn.addEventListener('click', () => {
        this.closeSizeGuide();
      });
    });

    // Close size guide on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.elements.sizeGuideDrawer && !this.elements.sizeGuideDrawer.hidden) {
        this.closeSizeGuide();
      }
    });
  }

  // Size guide methods
  openSizeGuide() {
    if (!this.elements.sizeGuideDrawer) return;
    this.elements.sizeGuideDrawer.hidden = false;
    // Trigger animation on next frame
    requestAnimationFrame(() => {
      this.elements.sizeGuideDrawer.classList.add('is-open');
    });
    document.body.style.overflow = 'hidden';
  }

  closeSizeGuide() {
    if (!this.elements.sizeGuideDrawer) return;
    this.elements.sizeGuideDrawer.classList.remove('is-open');
    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.elements.sizeGuideDrawer.hidden = true;
    }, 300);
    document.body.style.overflow = '';
  }

  // State management methods
  updateColorQuantity(color, delta) {
    const currentQty = this.getColorQuantity(color);
    const newQty = Math.max(0, currentQty + delta);
    const bundleQty = this.getBundleQuantity();
    const totalSelected = this.getTotalSelected();

    // Check if adding would exceed bundle size
    if (delta > 0 && totalSelected >= bundleQty) {
      return; // Can't add more
    }

    // Update or add item
    const existingItem = this.state.items.find(item => item.color === color);
    if (existingItem) {
      if (newQty === 0) {
        this.state.items = this.state.items.filter(item => item.color !== color);
      } else {
        existingItem.quantity = newQty;
      }
    } else if (newQty > 0) {
      this.state.items.push({ color, quantity: newQty });
    }

    this.render();
  }

  getColorQuantity(color) {
    const item = this.state.items.find(item => item.color === color);
    return item ? item.quantity : 0;
  }

  getTotalSelected() {
    return this.state.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  getBundleQuantity() {
    return this.config.bundles[this.state.bundleSize]?.qty || 6;
  }

  setSize(size) {
    this.state.selectedSize = size;
    this.render();
  }

  setBundleSize(size) {
    const oldQty = this.getBundleQuantity();
    this.state.bundleSize = size;
    const newQty = this.getBundleQuantity();

    // If downsizing, remove excess items (LIFO)
    if (newQty < oldQty) {
      let toRemove = this.getTotalSelected() - newQty;
      while (toRemove > 0 && this.state.items.length > 0) {
        const lastItem = this.state.items[this.state.items.length - 1];
        if (lastItem.quantity <= toRemove) {
          toRemove -= lastItem.quantity;
          this.state.items.pop();
        } else {
          lastItem.quantity -= toRemove;
          toRemove = 0;
        }
      }
    }

    this.render();
  }

  upgradeBundle() {
    const current = this.state.bundleSize;
    if (current === 'small') {
      this.setBundleSize('medium');
      this.section.querySelector('[data-bundle-radio][value="medium"]').checked = true;
    } else if (current === 'medium') {
      this.setBundleSize('large');
      this.section.querySelector('[data-bundle-radio][value="large"]').checked = true;
    }
  }

  downgradeBundle() {
    const current = this.state.bundleSize;
    if (current === 'large') {
      this.setBundleSize('medium');
      this.section.querySelector('[data-bundle-radio][value="medium"]').checked = true;
    } else if (current === 'medium') {
      this.setBundleSize('small');
      this.section.querySelector('[data-bundle-radio][value="small"]').checked = true;
    }
  }

  // Product dropdown methods
  populateProductDropdown() {
    if (!this.elements.productList || !this.config.products) return;

    this.elements.productList.innerHTML = this.config.products.map(product => `
      <button
        type="button"
        class="sticky-bar__dropdown-item${product.handle === this.state.productHandle ? ' is-active' : ''}"
        data-product-handle="${product.handle}"
      >
        ${product.title}
      </button>
    `).join('');

    // Bind click events
    this.elements.productList.querySelectorAll('[data-product-handle]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.switchProduct(btn.dataset.productHandle);
        this.closeProductDropdown();
      });
    });

    // With only one product there is nothing to switch to: hide the arrow and disable the selector
    const wrapper = this.section.querySelector('[data-product-selector-wrapper]');
    wrapper?.classList.toggle('sticky-bar__product--single', this.config.products.length <= 1);
  }

  toggleProductDropdown() {
    const dropdown = this.elements.productDropdown;
    const wrapper = this.section.querySelector('[data-product-selector-wrapper]');

    if (dropdown.hidden) {
      dropdown.hidden = false;
      wrapper.setAttribute('data-dropdown-open', '');
    } else {
      this.closeProductDropdown();
    }
  }

  closeProductDropdown() {
    this.elements.productDropdown.hidden = true;
    this.section.querySelector('[data-product-selector-wrapper]')?.removeAttribute('data-dropdown-open');
  }

  async switchProduct(handle) {
    if (handle === this.state.productHandle) return;

    await this.loadProduct(handle);
    this.setInitialSize();
    this.render();
    this.updateProductDropdownActive();
  }

  updateProductDropdownActive() {
    this.elements.productList?.querySelectorAll('[data-product-handle]').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.productHandle === this.state.productHandle);
    });
  }

  // UI update methods
  updateColorSelector() {
    if (!this.state.productData || !this.elements.colorGrid) return;

    const product = this.state.productData;
    const colors = this.getUniqueColors();

    this.elements.colorGrid.innerHTML = colors.map(color => {
      const variant = this.getVariantByColor(color);
      const available = this.isColorAvailable(color);
      const image = variant?.featured_image || product.featured_image;

      return `
        <div
          class="color-selector__card${!available ? ' color-selector__card--sold-out' : ''}"
          data-color-card
          data-color="${color}"
          data-available="${available}"
        >
          <div class="color-selector__badge" data-badge hidden></div>
          ${image ? `
            <img
              class="color-selector__image"
              src="${this.getImageUrl(image, 300)}"
              alt="${color}"
              loading="lazy"
              width="150"
              height="150"
            >
          ` : '<div class="color-selector__image color-selector__image--placeholder"></div>'}
          ${!available ? `
            <div class="color-selector__overlay">
              <span>${this.config.translations?.soldOut || 'Uitverkocht'}</span>
            </div>
          ` : ''}
          <div class="color-selector__quantity">
            <button
              type="button"
              class="color-selector__qty-btn color-selector__qty-btn--minus"
              data-qty-decrease
              aria-label="Verminder aantal"
              ${!available ? 'disabled' : ''}
            >
              <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="color-selector__qty-value" data-qty-value>0</span>
            <button
              type="button"
              class="color-selector__qty-btn color-selector__qty-btn--plus"
              data-qty-increase
              aria-label="Verhoog aantal"
              ${!available ? 'disabled' : ''}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  updateSizeSelector() {
    if (!this.state.productData || !this.elements.sizeButtons) return;

    const sizes = this.getUniqueSizes();

    this.elements.sizeButtons.innerHTML = sizes.map((size, index) => {
      const available = this.isSizeAvailable(size);
      const selected = size === this.state.selectedSize;

      return `
        <button
          type="button"
          class="size-selector__button${selected ? ' size-selector__button--selected' : ''}${!available ? ' size-selector__button--unavailable' : ''}"
          data-size-button
          data-size="${size}"
          data-available="${available}"
          ${!available ? 'disabled' : ''}
          aria-pressed="${selected}"
        >
          ${size}
        </button>
      `;
    }).join('');

    // Update size select (mobile)
    if (this.elements.sizeSelect) {
      this.elements.sizeSelect.innerHTML = sizes.map(size => {
        const available = this.isSizeAvailable(size);
        return `<option value="${size}" ${!available ? 'disabled' : ''}>${size}</option>`;
      }).join('');

      if (this.state.selectedSize) {
        this.elements.sizeSelect.value = this.state.selectedSize;
      }
    }
  }

  render() {
    this.renderBundlePreview();
    this.renderColorQuantities();
    this.renderSizeSelection();
    this.renderStickyBar();
    this.renderUpgradeBanner();
    this.renderDowngradeBanner();
    this.checkInventoryWarnings();
  }

  renderBundlePreview() {
    if (!this.elements.bundleGrid) return;

    const bundleQty = this.getBundleQuantity();
    const pricePerItem = this.calculatePricePerItem();
    const unitSuffix = this.config.translations?.unitPriceSuffix || '/st.';
    const priceText = pricePerItem > 0 ? `${this.formatMoney(pricePerItem)}${unitSuffix}` : '';
    const slots = [];

    // Placeholder SVG for empty slots — references the shared <symbol> rendered once in
    // the bundle-preview snippet (see mix-match-bundle-preview.liquid). Inlining the full
    // path per slot blew up iOS Safari memory and crashed the page on repeated re-renders.
    const placeholderSvg = `
      <svg class="bundle-preview__placeholder" viewBox="0 0 867 633" aria-hidden="true" focusable="false">
        <use href="#boxr-placeholder-symbol"></use>
      </svg>
    `;

    // Create filled slots for selected items
    this.state.items.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        const variant = this.getVariantByColor(item.color);
        const image = variant?.featured_image || this.state.productData?.featured_image;

        slots.push(`
          <div class="bundle-preview__slot bundle-preview__slot--filled" data-slot data-color="${item.color}">
            <button type="button" class="bundle-preview__remove" data-remove-item aria-label="Verwijder item">
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1H9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <div class="bundle-preview__slot-inner">
              ${image ? `<img class="bundle-preview__image" src="${this.getImageUrl(image, 300)}" alt="${item.color}" loading="lazy">` : placeholderSvg}
              <span class="bundle-preview__price">${priceText}</span>
            </div>
          </div>
        `);
      }
    });

    // Fill remaining with empty slots
    while (slots.length < bundleQty) {
      slots.push(`
        <div class="bundle-preview__slot bundle-preview__slot--empty" data-slot>
          <div class="bundle-preview__slot-inner">
            ${placeholderSvg}
            <span class="bundle-preview__price">${priceText}</span>
          </div>
        </div>
      `);
    }

    this.elements.bundleGrid.innerHTML = slots.join('');
  }

  renderColorQuantities() {
    const cards = this.elements.colorGrid?.querySelectorAll('[data-color-card]');
    if (!cards) return;

    const bundleQty = this.getBundleQuantity();
    const totalSelected = this.getTotalSelected();
    const maxReached = totalSelected >= bundleQty;

    cards.forEach(card => {
      const color = card.dataset.color;
      const qty = this.getColorQuantity(color);
      const qtyValue = card.querySelector('[data-qty-value]');
      const plusBtn = card.querySelector('[data-qty-increase]');

      if (qtyValue) qtyValue.textContent = qty;

      // Disable plus button once the bundle is full (no more items can be added to any color)
      if (plusBtn && card.dataset.available === 'true') {
        plusBtn.disabled = maxReached;
      }

      // Add active state if has quantity
      card.classList.toggle('color-selector__card--active', qty > 0);

      // Check if this color is unavailable in selected size
      if (this.state.selectedSize && qty > 0) {
        const variantAvailable = this.isVariantAvailable(color, this.state.selectedSize);
        card.classList.toggle('color-selector__card--unavailable', !variantAvailable);
      } else {
        card.classList.remove('color-selector__card--unavailable');
      }
    });
  }

  renderSizeSelection() {
    const buttons = this.elements.sizeButtons?.querySelectorAll('[data-size-button]');

    const selectedSizeEl = this.section.querySelector('[data-selected-size]');
    if (selectedSizeEl) {
      selectedSizeEl.textContent = this.state.selectedSize || '';
    }

    buttons?.forEach(btn => {
      const isSelected = btn.dataset.size === this.state.selectedSize;
      btn.classList.toggle('size-selector__button--selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected);
    });

    // Update size select (mobile)
    if (this.elements.sizeSelect && this.state.selectedSize) {
      this.elements.sizeSelect.value = this.state.selectedSize;
    }
  }

  renderStickyBar() {
    const totalSelected = this.getTotalSelected();
    const bundleQty = this.getBundleQuantity();
    const isComplete = totalSelected === bundleQty;

    // Update product info
    const productName = this.section.querySelector('[data-product-name]');
    const productThumbnail = this.section.querySelector('[data-product-thumbnail]');

    if (productName) {
      const productConfig = this.config.products?.find(p => p.handle === this.state.productHandle);
      productName.textContent = productConfig?.title || this.state.productData?.title || '';
    }

    if (productThumbnail && this.state.productData?.featured_image) {
      if (productThumbnail.tagName === 'IMG') {
        productThumbnail.src = this.getImageUrl(this.state.productData.featured_image, 80);
      }
    }

    // Update pack indicator
    const packQty = this.section.querySelector('[data-pack-qty]');
    if (packQty) packQty.textContent = bundleQty;

    // Update size indicator
    const sizeIndicator = this.section.querySelector('[data-size-indicator]');
    if (sizeIndicator) sizeIndicator.textContent = this.state.selectedSize || '-';

    // Update progress
    const progressCurrent = this.section.querySelector('[data-progress-current]');
    const progressTotal = this.section.querySelector('[data-progress-total]');
    if (progressCurrent) progressCurrent.textContent = totalSelected;
    if (progressTotal) progressTotal.textContent = bundleQty;

    // Update bundle select (mobile)
    if (this.elements.bundleSelect) {
      this.elements.bundleSelect.value = this.state.bundleSize;
    }

    // Update prices (only show the "from" price when there is an actual saving)
    const { originalPrice, finalPrice } = this.calculatePrices();
    const originalText = originalPrice > finalPrice ? this.formatMoney(originalPrice) : '';
    const originalPriceEl = this.section.querySelector('[data-original-price]');
    const finalPriceEl = this.section.querySelector('[data-final-price]');

    if (originalPriceEl) {
      originalPriceEl.textContent = originalText;
    }
    if (finalPriceEl) {
      finalPriceEl.textContent = this.formatMoney(finalPrice);
    }

    // Update mobile prices
    if (this.elements.originalPriceMobile) {
      this.elements.originalPriceMobile.textContent = originalText;
    }
    if (this.elements.finalPriceMobile) {
      this.elements.finalPriceMobile.textContent = this.formatMoney(finalPrice);
    }

    // Update CTA button
    const ctaText = this.section.querySelector('[data-cta-text]');
    const remaining = bundleQty - totalSelected;

    if (ctaText) {
      if (isComplete) {
        ctaText.textContent = this.config.translations?.addToCart || 'Voeg toe';
      } else {
        const selectMoreText = this.config.translations?.selectMore || 'Selecteer';
        const moreText = this.config.translations?.more || 'meer';
        ctaText.textContent = `${selectMoreText} ${remaining} ${moreText}`;
      }
    }

    // Enable/disable button
    const hasInventoryIssues = this.hasInventoryConflicts();
    this.elements.addToCartBtn.disabled = !isComplete || hasInventoryIssues;
  }

  renderUpgradeBanner() {
    if (!this.elements.upgradeBanner) return;

    const currentSize = this.state.bundleSize;

    // Hide if already on largest bundle
    if (currentSize === 'large') {
      this.elements.upgradeBanner.hidden = true;
      return;
    }

    // Determine next bundle
    const nextSize = currentSize === 'small' ? 'medium' : 'large';
    const nextQty = this.config.bundles[nextSize]?.qty || 0;

    // Price per item for the next bundle, derived from its pack price
    const discountedPrice = this.getPackPricePerItem(nextSize);

    const titleEl = this.elements.upgradeBanner.querySelector('[data-upgrade-title]');
    const priceEl = this.elements.upgradeBanner.querySelector('[data-upgrade-price]');

    if (titleEl) {
      titleEl.textContent = `${this.config.translations?.upgradeText || 'Upgrade naar'} ${nextQty} ${this.config.translations?.itemsText || 'items'}`;
    }
    if (priceEl) {
      priceEl.textContent = `${this.config.translations?.forOnly || 'Voor slechts'} ${this.formatMoney(discountedPrice)}/${this.config.translations?.perItem || 'per stuk'}`;
    }

    this.elements.upgradeBanner.hidden = false;
  }

  renderDowngradeBanner() {
    if (!this.elements.downgradeBanner) return;

    const currentSize = this.state.bundleSize;

    // Hide if already on smallest bundle
    if (currentSize === 'small') {
      this.elements.downgradeBanner.hidden = true;
      return;
    }

    // Determine next bundle down
    const prevSize = currentSize === 'large' ? 'medium' : 'small';
    const prevQty = this.config.bundles[prevSize]?.qty || 0;

    // Price per item for the smaller bundle, derived from its pack price
    const discountedPrice = this.getPackPricePerItem(prevSize);

    const titleEl = this.elements.downgradeBanner.querySelector('[data-downgrade-title]');
    const priceEl = this.elements.downgradeBanner.querySelector('[data-downgrade-price]');

    if (titleEl) {
      titleEl.textContent = `${this.config.translations?.downgradeText || 'Downgrade naar'} ${prevQty} ${this.config.translations?.itemsText || 'items'}`;
    }
    if (priceEl) {
      priceEl.textContent = `${this.formatMoney(discountedPrice)}/${this.config.translations?.perItem || 'per stuk'}`;
    }

    this.elements.downgradeBanner.hidden = false;
  }

  checkInventoryWarnings() {
    if (!this.state.selectedSize) return;

    const unavailableColors = [];

    this.state.items.forEach(item => {
      if (!this.isVariantAvailable(item.color, this.state.selectedSize)) {
        unavailableColors.push(item.color);
      }
    });

    if (unavailableColors.length > 0) {
      const warningText = `${this.config.translations?.warningText || 'Let op: De volgende kleuren zijn niet beschikbaar in maat'} ${this.state.selectedSize}: ${unavailableColors.join(', ')}. ${this.config.translations?.removeOrChange || 'Verwijder deze kleuren of kies een andere maat.'}`;

      if (this.elements.warningText) {
        this.elements.warningText.textContent = warningText;
      }
      this.elements.warning?.removeAttribute('hidden');
    } else {
      this.elements.warning?.setAttribute('hidden', '');
    }
  }

  hasInventoryConflicts() {
    if (!this.state.selectedSize) return false;

    return this.state.items.some(item =>
      !this.isVariantAvailable(item.color, this.state.selectedSize)
    );
  }

  // Helper methods
  getUniqueColors() {
    if (!this.state.productData) return [];
    const colors = new Set();
    this.state.productData.variants.forEach(v => colors.add(v.option1));
    return Array.from(colors);
  }

  getUniqueSizes() {
    if (!this.state.productData) return [];
    const sizes = new Set();
    this.state.productData.variants.forEach(v => {
      if (v.option2) sizes.add(v.option2);
    });
    return Array.from(sizes);
  }

  getVariantByColor(color) {
    if (!this.state.productData) return null;
    return this.state.productData.variants.find(v => v.option1 === color);
  }

  getVariant(color, size) {
    if (!this.state.productData) return null;
    return this.state.productData.variants.find(v =>
      v.option1 === color && v.option2 === size
    );
  }

  isColorAvailable(color) {
    if (!this.state.productData) return false;
    return this.state.productData.variants.some(v =>
      v.option1 === color && v.available
    );
  }

  isSizeAvailable(size) {
    if (!this.state.productData) return false;
    return this.state.productData.variants.some(v =>
      v.option2 === size && v.available
    );
  }

  isVariantAvailable(color, size) {
    const variant = this.getVariant(color, size);
    return variant?.available || false;
  }

  getImageUrl(image, width) {
    if (typeof image === 'string') {
      // Simple URL, add size parameter
      return image.replace(/(\.[^.]+)$/, `_${width}x$1`);
    }
    // Object with src property
    return image.src?.replace(/(\.[^.]+)$/, `_${width}x$1`) || '';
  }

  getPack(size) {
    const product = this.config.products?.find(p => p.handle === this.state.productHandle);
    const pack = product?.packs?.[size];
    if (pack && pack.price != null) return pack;

    const base = this.state.productData?.price || 0;
    const qty = this.config.bundles?.[size]?.qty || 0;
    const total = base * qty;
    return { price: total, compareAt: total };
  }

  getPackPricePerItem(size) {
    const qty = this.config.bundles?.[size]?.qty || 0;
    if (qty === 0) return 0;
    return this.getPack(size).price / qty;
  }

  calculatePrices() {
    const size = this.state.bundleSize;
    const bundleQty = this.getBundleQuantity();
    const totalSelected = this.getTotalSelected();
    const pack = this.getPack(size);

    const originalTotal = pack.compareAt && pack.compareAt > pack.price ? pack.compareAt : pack.price;
    const perItemFinal = bundleQty > 0 ? pack.price / bundleQty : 0;
    const perItemOriginal = bundleQty > 0 ? originalTotal / bundleQty : 0;

    const finalPrice = perItemFinal * totalSelected;
    const originalPrice = perItemOriginal * totalSelected;

    return { originalPrice, finalPrice, discountAmount: originalPrice - finalPrice };
  }

  calculatePricePerItem() {
    const { finalPrice } = this.calculatePrices();
    const totalSelected = this.getTotalSelected();
    if (totalSelected === 0) return 0;
    return Math.round(finalPrice / totalSelected);
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  // Fetch wrapper that retries on a 429 (cart API rate limit) with a short backoff
  async fetchWithRetry(url, options, attempts = 3) {
    let response;
    for (let i = 0; i < attempts; i++) {
      response = await fetch(url, options);
      if (response.status !== 429) return response;
      await new Promise((resolve) => setTimeout(resolve, 600 * (i + 1)));
    }
    return response;
  }

  // Cart functionality
  async addToCart() {
    if (this.hasInventoryConflicts()) {
      return;
    }

    const bundleId = Date.now().toString();
    const items = [];

    // Build cart items array
    this.state.items.forEach(item => {
      const variant = this.getVariant(item.color, this.state.selectedSize);
      if (variant) {
        items.push({
          id: variant.id,
          quantity: item.quantity,
          properties: {
            '_bundle_id': bundleId,
            '_bundle_size': this.getBundleQuantity(),
          },
        });
      }
    });

    if (items.length === 0) return;

    // Disable button during request
    this.elements.addToCartBtn.disabled = true;
    const originalText = this.section.querySelector('[data-cta-text]')?.textContent;
    const ctaText = this.section.querySelector('[data-cta-text]');
    if (ctaText) ctaText.textContent = '...';

    try {
      const response = await this.fetchWithRetry('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Fetch updated cart with sections to refresh cart UI
      const cartDrawer = document.querySelector('cart-drawer-component');
      const sectionId = cartDrawer?.querySelector('cart-items-component')?.dataset?.sectionId || 'cart-drawer';

      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      const sectionsData = await sectionsResponse.json();

      // Dispatch cart:update event with sections data
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          source: 'mix-match',
          data: {
            sections: sectionsData,
          },
        },
      }));

      // Open cart drawer
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      // Reset selection
      this.state.items = [];
      this.render();

      // Show success feedback
      if (ctaText) ctaText.textContent = this.config.translations?.added || 'Toegevoegd!';
      setTimeout(() => {
        if (ctaText) ctaText.textContent = originalText;
      }, 2000);

    } catch (error) {
      console.error('Add to cart failed:', error);
      if (ctaText) ctaText.textContent = this.config.translations?.errorTryAgain || 'Fout - probeer opnieuw';
      setTimeout(() => {
        if (ctaText) ctaText.textContent = originalText;
        this.elements.addToCartBtn.disabled = false;
      }, 2000);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('.mix-match');
  if (section) {
    new MixMatch(section);
  }
});
